import React from 'react';
import { scoreTextOn, scoreToColor } from './areaData.js';
import { scoreWord } from './scoreWords.js';

// The little looping scenes of the intro tips (IntroTips.js; the owner's pick of the 2026-11-02 "Introrutor"
// canvas): each acts out one thing on a 340×200 stage — a pointer (a finger dot on touch screens) clicks, drags or
// points and the site answers — so a tip needs a title and one line, not a wall of text. Kartvy has four
// (pick, the colours, comparing, the address pin), listvy four (several places, the score filter, the map, the
// card), and both views share the welcome and two tips that come when something happens (travel time, list or
// map; it-g1-… to it-g3-…). The animations are CSS keyframes in introTips.css, named after the scene (it-k1-…, it-l3-…); each scene's
// `still` is the moment shown with reduced motion.

const AREAS = [
    { points: '0,0 120,0 95,80 0,90', score: 4 },
    { points: '120,0 230,0 260,75 175,100 95,80', score: 5 },
    { points: '230,0 340,0 340,95 260,75', score: 3 },
    { points: '0,90 95,80 130,150 80,200 0,200', score: 3.5 },
    { points: '95,80 175,100 205,150 150,200 80,200 130,150', score: 4.5 },
    { points: '175,100 260,75 340,95 340,200 240,200 205,150', score: 2 },
    { points: '205,150 240,200 150,200', score: 5.5 },
];
const P2 = '120,0 230,0 260,75 175,100 95,80';
const P3 = '230,0 340,0 340,95 260,75';
const P5 = '95,80 175,100 205,150 150,200 80,200 130,150';
const P2_P5 = '120,0 230,0 260,75 175,100 205,150 150,200 80,200 130,150 95,80';
const SCALE_STEPS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6];

const decimal = (value, sv) => (sv ? String(value).replace('.', ',') : String(value));
const areaLabel = (score, sv) => `${scoreWord(score, sv)} ${sv ? 'område' : 'area'}`;
const scoreStyle = (score) => ({ background: scoreToColor[score], color: scoreTextOn(scoreToColor[score]) });

const AreaMap = ({ filled = false, opacity = 0.62, children }) => (
    <svg className="it-map" viewBox="0 0 340 200" aria-hidden="true">
        {AREAS.map(({ points, score }) => (filled ? (
            <polygon key={points} className="it-area-edge" points={points} fill={scoreToColor[score]} fillOpacity={opacity} />
        ) : (
            <polygon key={points} points={points} fill={scoreToColor[score]} fillOpacity={0.2} stroke={scoreToColor[score]} strokeWidth="1.4" strokeLinejoin="round" />
        )))}
        <g className="it-roads">
            <path d="M-5 122 C 80 112, 160 132, 345 110" strokeWidth="3" />
            <path d="M196 -5 C 186 60, 212 140, 188 205" strokeWidth="2.5" />
        </g>
        {children}
    </svg>
);

const StreetMap = ({ children }) => (
    <svg className="it-map" viewBox="0 0 340 200" aria-hidden="true">
        <path className="it-water" d="M246 205 C 262 168, 310 156, 345 146 L345 205 Z" />
        <g className="it-buildings">
            <rect x="18" y="16" width="18" height="12" rx="1" /><rect x="44" y="26" width="13" height="16" rx="1" />
            <rect x="132" y="24" width="20" height="13" rx="1" /><rect x="162" y="14" width="13" height="11" rx="1" />
            <rect x="138" y="84" width="22" height="14" rx="1" /><rect x="168" y="80" width="12" height="16" rx="1" />
            <rect x="232" y="30" width="18" height="13" rx="1" /><rect x="250" y="62" width="14" height="12" rx="1" />
            <rect x="26" y="112" width="16" height="12" rx="1" /><rect x="52" y="130" width="13" height="14" rx="1" />
            <rect x="150" y="140" width="16" height="12" rx="1" /><rect x="226" y="122" width="15" height="13" rx="1" />
            <rect x="180" y="176" width="18" height="11" rx="1" />
        </g>
        <g className="it-streets">
            <path className="is-casing" d="M-5 66 L345 106" strokeWidth="10" />
            <path d="M-5 66 L345 106" strokeWidth="7" />
            <path className="is-casing" d="M100 -5 L124 205" strokeWidth="8" />
            <path d="M100 -5 L124 205" strokeWidth="5" />
            <path d="M210 -5 C 196 70, 232 140, 206 205" strokeWidth="4" />
            <path d="M-5 158 L345 170" strokeWidth="3.5" />
        </g>
        {children}
    </svg>
);

// The site's house markers (shapeMarkers.js): a block of flats in the flats' blue, a villa in the villas' green
// (`filled`: the visited look, the body in the colour and the door and windows white).
const HomeIcon = ({ kind, filled = false }) => (kind === 'villa' ? (
    <svg viewBox="0 0 28 28" aria-hidden="true">
        <path d="M3.5 25.5V13L14 4.5l10.5 8.5v12.5Z" fill={filled ? '#1f7a62' : '#fff'} stroke="#1f7a62" strokeWidth="2.6" strokeLinejoin="round" />
        <path d="M12 25.5v-6a2 2 0 0 1 4 0v6Z" fill={filled ? '#fff' : '#1f7a62'} />
        <rect x="7" y="14" width="3.6" height="3.4" rx=".6" fill={filled ? '#fff' : '#1f7a62'} />
        <rect x="17.4" y="14" width="3.6" height="3.4" rx=".6" fill={filled ? '#fff' : '#1f7a62'} />
    </svg>
) : (
    <svg viewBox="0 0 28 28" aria-hidden="true">
        <path d="M4 26V9.5L14 3l10 6.5V26Z" fill="#fff" stroke="#4a96d0" strokeWidth="2.6" strokeLinejoin="round" />
        <g fill="#4a96d0">
            <rect x="8" y="11" width="4" height="3.4" rx=".6" /><rect x="16" y="11" width="4" height="3.4" rx=".6" />
            <rect x="8" y="17" width="4" height="3.4" rx=".6" /><rect x="16" y="17" width="4" height="3.4" rx=".6" />
        </g>
    </svg>
));

const Home = ({ kind = 'flat', filled, x, y, className = '', delay }) => (
    <span className={`it-home ${className}`} style={{ left: x, top: y, animationDelay: delay }}>
        <HomeIcon kind={kind} filled={filled} />
    </span>
);

const Cursor = ({ className }) => (
    <span className={`it-cur ${className}`} aria-hidden="true">
        <svg className="it-cur-arrow" viewBox="0 0 18 22">
            <path d="M2 1.5v16.2l4.3-4 2.9 6.4 2.6-1.1-2.8-6.3 5.9-.4Z" />
        </svg>
        <span className="it-cur-finger" />
    </span>
);

const Ripple = ({ x, y, className }) => <span className={`it-rip ${className}`} style={{ left: x, top: y }} />;

const SearchGlyph = () => (
    <svg className="it-search-glyph" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m15.5 15.5 5 5" />
    </svg>
);

const ScaleBar = ({ className }) => (
    <span className={`it-scale ${className}`}>
        {SCALE_STEPS.map((step) => <i key={step} style={{ background: scoreToColor[step] }} />)}
    </span>
);

const PIN_PATH = 'M13 1.5C6.6 1.5 1.5 6.6 1.5 13c0 8.6 11.5 21 11.5 21s11.5-12.4 11.5-21C24.5 6.6 19.4 1.5 13 1.5Z';

// Small drawn "photos" for the listing cards.
const Photo = ({ kind }) => {
    if (kind === 'flat') {
        return (
            <svg viewBox="0 0 40 40" aria-hidden="true">
                <rect width="40" height="40" fill="#d7e6ee" /><rect x="8" y="6" width="24" height="34" fill="#e9e3d9" />
                <g fill="#7d93a3">
                    <rect x="12" y="10" width="4" height="4" /><rect x="24" y="10" width="4" height="4" />
                    <rect x="12" y="18" width="4" height="4" /><rect x="24" y="18" width="4" height="4" />
                    <rect x="12" y="26" width="4" height="4" /><rect x="24" y="26" width="4" height="4" />
                </g>
            </svg>
        );
    }
    if (kind === 'room') {
        return (
            <svg viewBox="0 0 40 40" aria-hidden="true">
                <rect width="40" height="40" fill="#efe4d3" />
                <rect x="6" y="6" width="14" height="16" fill="#cfe3ef" stroke="#fff" strokeWidth="2" />
                <rect y="30" width="40" height="10" fill="#c9a77f" /><rect x="20" y="22" width="16" height="9" rx="2" fill="#7a8f8a" />
            </svg>
        );
    }
    if (kind === 'cottage') {
        return (
            <svg viewBox="0 0 40 40" aria-hidden="true">
                <rect width="40" height="40" fill="#d8e8f0" /><rect y="28" width="40" height="12" fill="#a4c493" />
                <rect x="10" y="16" width="20" height="14" fill="#b23a2e" /><path d="M8 17 20 8l12 9Z" fill="#3d3d3d" />
                <rect x="14" y="20" width="4" height="4" fill="#fff" /><rect x="22" y="20" width="4" height="4" fill="#fff" />
            </svg>
        );
    }
    return (
        <svg viewBox="0 0 40 40" aria-hidden="true">
            <rect width="40" height="40" fill="#cfe3ef" /><rect y="27" width="40" height="13" fill="#9dbd8c" />
            <rect x="9" y="15" width="22" height="15" fill="#efe6d6" /><path d="M6 16 20 6l14 10Z" fill="#a85a43" />
            <rect x="13" y="19" width="4" height="4" fill="#6f8796" /><rect x="23" y="19" width="4" height="4" fill="#6f8796" />
        </svg>
    );
};

const MiniCard = ({ className = '', style, photo, address, price, score, sv, children }) => (
    <span className={`it-mc ${className}`} style={style}>
        <span className="it-mc-photo"><Photo kind={photo} /></span>
        <span className="it-mc-text">
            <span className="it-mc-address">{address}</span>
            {price && <span className="it-mc-price">{price}</span>}
            {children}
        </span>
        {score != null && (
            <span className="it-mc-score"><i style={{ background: scoreToColor[score] }} />{decimal(score, sv)}</span>
        )}
    </span>
);

// Kartvy 1: a search for an area shows its homes; a click on the area next to it adds it.
const KartvyPick = ({ sv }) => (
    <>
        <AreaMap>
            <path className="it-dim it-k1-dim" d={`M0 0H340V200H0Z M${P2_P5.replace(/ /g, 'L')}Z`} fillRule="evenodd" />
            <polygon className="it-dim it-k1-patch" points={P5} />
            <polygon className="it-pick-line it-k1-ol1" points={P2} />
            <polygon className="it-pick-line it-k1-ol2" points={P2_P5} />
        </AreaMap>
        <Home x={226} y={30} className="it-k1-hA" />
        <Home x={240} y={54} className="it-k1-hA" delay=".12s" />
        <Home kind="villa" x={205} y={72} className="it-k1-hA" delay=".24s" />
        <Home x={150} y={92} className="it-k1-hA" delay=".36s" />
        <Home kind="villa" x={140} y={128} className="it-k1-hB" />
        <Home x={166} y={174} className="it-k1-hB" delay=".15s" />
        <span className="it-search it-k1-search">
            <SearchGlyph />
            <span className="it-field">
                <span className="it-ph it-k1-ph">{sv ? 'Sök adress, ort eller område' : 'Search address, town or area'}</span>
                <span className="it-type it-k1-type">Gustav Vasa</span>
            </span>
        </span>
        <span className="it-drop it-k1-drop">
            <span className="it-drop-label">{sv ? 'Visa bara bostäder i' : 'Only show homes in'}</span>
            <span className="it-drop-row">Gustav Vasa<span>{sv ? 'Område' : 'Area'}</span></span>
        </span>
        <span className="it-chips it-k1-chips">
            <span className="it-chip is-green it-k1-chip1">Gustav Vasa<span aria-hidden="true">✕</span></span>
            <span className="it-chip is-green it-k1-chip2">Norra Johannes<span aria-hidden="true">✕</span></span>
        </span>
        <Ripple x={110} y={81} className="it-k1-r1" />
        <Ripple x={150} y={141} className="it-k1-r2" />
        <Cursor className="it-k1-cur" />
    </>
);

// Kartvy 2: pointing at an area shows its card, the score on the colour scale; another area, the mark slides.
const KartvyScore = ({ sv }) => (
    <>
        <AreaMap filled opacity={0.66}>
            <polygon className="it-hover it-k2-hl1" points={P2} />
            <polygon className="it-hover it-k2-hl2" points={P3} />
        </AreaMap>
        <span className="it-panel it-k2-card">
            <span className="it-k2-row it-k2-a">
                <span className="it-badge" style={scoreStyle(5)}>5</span>
                <span className="it-k2-names"><b>Hedvig Eleonora</b><span>{areaLabel(5, sv)}</span></span>
            </span>
            <span className="it-k2-row it-k2-b">
                <span className="it-badge" style={scoreStyle(3)}>3</span>
                <span className="it-k2-names"><b>Gubbängen</b><span>{areaLabel(3, sv)}</span></span>
            </span>
            <ScaleBar className="it-k2-scale" />
            <span className="it-mark it-k2-mk" />
        </span>
        <Cursor className="it-k2-cur" />
    </>
);

// Kartvy 3: double-clicking two areas numbers them and puts them in columns; the best values get a tag.
const KartvyCompare = ({ sv, touch }) => (
    <>
        <AreaMap filled opacity={0.58} />
        <Ripple x={50} y={42} className="it-k3-r1" />
        <Ripple x={294} y={42} className="it-k3-r2" />
        <span className="it-disc it-k3-d1" style={{ left: 50, top: 42 }}>1</span>
        <span className="it-disc it-k3-d2" style={{ left: 294, top: 42 }}>2</span>
        <span className="it-panel it-k3-card">
            <span className="it-k3-labels">
                <span>{sv ? 'Betyg' : 'Score'}</span>
                <span>{sv ? 'Inkomst' : 'Income'}</span>
                <span>{sv ? 'Arbetslöshet' : 'Unemployment'}</span>
            </span>
            <span className="it-k3-col it-k3-c1">
                <span className="it-k3-head"><span className="it-mini-disc">1</span>Gärdet</span>
                <span className="it-k3-cells">
                    <span><span className="it-chip-score" style={scoreStyle(4.5)}>{decimal(4.5, sv)}</span></span>
                    <span>{sv ? '40 tkr' : '40k kr'}<span className="it-tag it-k3-t1">{sv ? 'Högst' : 'Highest'}</span></span>
                    <span>{decimal(3.3, sv)} %</span>
                </span>
            </span>
            <span className="it-k3-ph">
                {touch
                    ? (sv ? 'Dubbeltryck på ett område till' : 'Double-tap another area')
                    : (sv ? 'Dubbelklicka på ett område till' : 'Double-click another area')}
            </span>
            <span className="it-k3-col it-k3-c2">
                <span className="it-k3-head"><span className="it-mini-disc">2</span>Liljeholmen</span>
                <span className="it-k3-cells">
                    <span><span className="it-chip-score" style={scoreStyle(4)}>4</span></span>
                    <span>{sv ? '36 tkr' : '36k kr'}</span>
                    <span>{decimal('3.0', sv)} %<span className="it-tag it-k3-t2">{sv ? 'Lägst' : 'Lowest'}</span></span>
                </span>
            </span>
        </span>
        <Cursor className="it-k3-cur" />
    </>
);

// Kartvy 4: the pin comes out of the Adress button and lands on a house, whose sales draw in. On touch screens
// the button is tapped and then the house, which is how the tool works there.
const KartvyAddress = ({ sv }) => (
    <>
        <StreetMap />
        <span className="it-k4-shadow" />
        <span className="it-k4-ring" />
        <span className="it-k4-capsule">
            <span className="it-k4-btn is-first">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4 3 9l9 5 9-5Z M3 14l9 5 9-5" /></svg>
            </span>
            <span className="it-k4-btn is-place">
                <svg className="it-k4-icon" viewBox="0 0 26 36" aria-hidden="true">
                    <path d="M13 2C6.9 2 2 6.9 2 13c0 8.2 11 20.5 11 20.5S24 21.2 24 13C24 6.9 19.1 2 13 2Z" fill="#fff" />
                    <circle cx="13" cy="13" r="4.2" fill="#1f7a62" />
                </svg>
            </span>
            <span className="it-k4-slot" />
            <span className="it-k4-label">{sv ? 'Adress' : 'Address'}</span>
        </span>
        <svg className="it-k4-pin" viewBox="0 0 26 36" aria-hidden="true">
            <path d={PIN_PATH} fill="#1f7a62" stroke="#fff" strokeWidth="2" />
            <circle cx="13" cy="13" r="4.4" fill="#fff" />
        </svg>
        <span className="it-panel it-k4-card">
            <b className="it-k4-address">Sveavägen 120</b>
            <span className="it-k4-sub">{sv ? 'Lägenheter · 134 000 kr/m²' : 'Flats · 134 000 kr/m²'}</span>
            <svg className="it-k4-spark" viewBox="0 0 176 22" aria-hidden="true">
                <path className="it-k4-line" d="M2 18 C 20 17, 30 15, 46 14 S 74 12, 90 10 S 120 9, 136 6 S 160 4, 174 3" />
                <circle className="it-k4-sale it-k4-s1" cx="60" cy="13" r="3.2" />
                <circle className="it-k4-sale it-k4-s2" cx="136" cy="6" r="3.2" />
            </svg>
            <span className="it-k4-caption">{sv ? '4 sålda i huset · senast 2026' : '4 sold in the building · last 2026'}</span>
        </span>
        <Ripple x={309} y={104} className="it-k4-rt1" />
        <Ripple x={150} y={96} className="it-k4-rt2" />
        <Cursor className="it-k4-cur" />
    </>
);

// Listvy 1: one place typed and picked, then another; both become chips and the count goes up.
const ListvyPlaces = ({ sv }) => {
    const homes = (n) => `${n} ${sv ? 'bostäder' : 'homes'}`;
    const rooms = (n) => `${n} ${sv ? 'rum' : 'rooms'}`;
    return (
        <>
            <span className="it-band" />
            <span className="it-search it-l1-search">
                <SearchGlyph />
                <span className="it-field">
                    <span className="it-ph it-l1-ph">{sv ? 'Sök ort, område eller gata' : 'Search town, area or street'}</span>
                    <span className="it-type it-l1-t1">Solna</span>
                    <span className="it-type it-l1-t2">Bromma</span>
                </span>
                <span className="it-search-go" />
            </span>
            <span className="it-chips it-l1-chips">
                <span className="it-chip it-l1-c1">{sv ? 'Solna kommun' : 'Solna'}<span aria-hidden="true">✕</span></span>
                <span className="it-chip it-l1-c2">Bromma<span aria-hidden="true">✕</span></span>
                <span className="it-chip is-dark it-l1-c1">{sv ? 'Rensa' : 'Clear'}</span>
            </span>
            <span className="it-suggestion it-l1-s1"><b>Solna</b><span>{sv ? 'Kommun' : 'Municipality'}</span></span>
            <span className="it-suggestion it-l1-s2">
                <span><b>Bromma</b><span className="it-muted"> · Stockholm</span></span>
                <span>{sv ? 'Postort · 14 områden' : 'Postal town · 14 areas'}</span>
            </span>
            <span className="it-count it-l1-n1">{homes(312)}</span>
            <span className="it-count it-l1-n2">{homes(548)}</span>
            <MiniCard className="it-l1-row it-l1-a" style={{ top: 114 }} photo="flat" address="Råsundavägen 12" price={`3 450 000 kr · ${rooms(2)}`} score={4} sv={sv} />
            <MiniCard className="it-l1-row it-l1-a" style={{ top: 156, animationDelay: '.12s' }} photo="villa" address="Huvudstagatan 4" price={`2 890 000 kr · ${rooms(2)}`} score={3} sv={sv} />
            <MiniCard className="it-l1-row it-l1-b" style={{ top: 114 }} photo="cottage" address="Brommaplan 3" price={`5 950 000 kr · ${rooms(4)}`} score={3.5} sv={sv} />
            <MiniCard className="it-l1-row it-l1-b" style={{ top: 156, animationDelay: '.12s' }} photo="flat" address="Råsundavägen 12" price={`3 450 000 kr · ${rooms(2)}`} score={4} sv={sv} />
            <Ripple x={62} y={63} className="it-l1-r1" />
            <Ripple x={62} y={63} className="it-l1-r2" />
            <Cursor className="it-l1-cur" />
        </>
    );
};

// Listvy 2: the score bar's handle is dragged to 4, Sök bostäder turns green, the homes below 4 go.
const ListvyScoreFilter = ({ sv }) => {
    const homes = (n) => `${n} ${sv ? 'bostäder' : 'homes'}`;
    const apply = sv ? 'Sök bostäder!' : 'Search listings!';
    return (
        <>
            <span className="it-panel it-l2-filter">
                <b className="it-l2-label" style={{ top: 12 }}>{sv ? 'Områdesbetyg' : 'Area score'}</b>
                <ScaleBar className="it-l2-scale" />
                <span className="it-l2-grey" />
                <span className="it-handle it-l2-hl" />
                <span className="it-handle it-l2-hr" />
                <span className="it-l2-v it-l2-v1">{sv ? 'Alla områden' : 'All areas'}</span>
                <span className="it-l2-v it-l2-v2">{sv ? 'Betyg 4 till 6' : 'Score 4 to 6'}</span>
                <b className="it-l2-label" style={{ top: 76 }}>{sv ? 'Pris' : 'Price'}</b>
                <svg className="it-l2-bars" viewBox="0 0 128 28" aria-hidden="true">
                    {[6, 12, 20, 26, 23, 18, 16, 13, 10, 8, 6, 5, 4, 3].map((h, i) => (
                        <rect key={i} x={i * 9} y={28 - h} width="7" height={h} rx="1" />
                    ))}
                </svg>
                <span className="it-l2-track" />
                <span className="it-l2-knob" style={{ left: 8 }} />
                <span className="it-l2-knob" style={{ left: 135 }} />
                <span className="it-l2-apply">{apply}</span>
                <span className="it-l2-apply is-on it-l2-b1">{apply}</span>
            </span>
            <span className="it-count it-l2-n it-l2-n1">{homes(548)}</span>
            <span className="it-count it-l2-n it-l2-n2">{homes(211)}</span>
            <MiniCard className="it-l2-row it-l2-out" style={{ top: 32 }} photo="villa" address="Vårbergsvägen 8" price="2 150 000 kr" score={1} sv={sv} />
            <MiniCard className="it-l2-row it-l2-up1" style={{ top: 70 }} photo="flat" address="Råsundavägen 12" price="3 450 000 kr" score={4} sv={sv} />
            <MiniCard className="it-l2-row it-l2-out" style={{ top: 108 }} photo="room" address="Gubbängsvägen 40" price="2 690 000 kr" score={3} sv={sv} />
            <MiniCard className="it-l2-row it-l2-up2" style={{ top: 146 }} photo="flat" address="Karlavägen 58" price="6 950 000 kr" score={5} sv={sv} />
            <Ripple x={88} y={165} className="it-l2-r" />
            <Cursor className="it-l2-cur" />
        </>
    );
};

const LISTVY_MAP_AREAS = [
    { points: '0,0 80,0 70,60 0,70', score: 4 },
    { points: '80,0 156,0 156,80 100,70 70,60', score: 4.5 },
    { points: '0,70 70,60 100,70 90,120 0,130', score: 3 },
    { points: '100,70 156,80 156,176 110,176 90,120', score: 2 },
    { points: '0,130 90,120 110,176 0,176', score: 3.5 },
];

// Listvy 3: pointing at a card lights its home on the map; Visa på kartan zooms in and the home hops.
const ListvyMap = ({ sv }) => {
    const show = sv ? 'Visa på kartan' : 'Show on map';
    return (
        <>
            <MiniCard className="it-l3-card" style={{ top: 12 }} photo="villa" address="Ekvägen 7" price="6 250 000 kr">
                <span className="it-l3-link">{show}</span>
            </MiniCard>
            <MiniCard className="it-l3-card" style={{ top: 72 }} photo="flat" address="Sveavägen 120" price="4 150 000 kr">
                <span className="it-l3-link it-l3-swap">
                    <span className="it-l3-l1">{show}</span>
                    <span className="it-l3-l2">{sv ? '✓ Visas på kartan' : '✓ Shown on the map'}</span>
                </span>
            </MiniCard>
            <span className="it-l3-hl" />
            <MiniCard className="it-l3-card" style={{ top: 132 }} photo="cottage" address="Stugvägen 2" price="1 950 000 kr">
                <span className="it-l3-link">{show}</span>
            </MiniCard>
            <span className="it-l3-map">
                <span className="it-l3-zoom">
                    <svg viewBox="0 0 156 176" aria-hidden="true">
                        {LISTVY_MAP_AREAS.map(({ points, score }) => (
                            <polygon key={points} className="it-area-edge" points={points} fill={scoreToColor[score]} fillOpacity={0.38} />
                        ))}
                        <g className="it-roads">
                            <path d="M-5 100 C 40 96, 100 104, 160 92" strokeWidth="3" />
                            <path d="M60 -5 C 56 60, 70 120, 64 180" strokeWidth="2.4" />
                        </g>
                    </svg>
                </span>
                <Home x={30} y={40} className="it-l3-m1" />
                <Home kind="villa" x={120} y={50} className="it-l3-m3" />
                <Home kind="villa" x={40} y={140} className="it-l3-m4" />
                <Home x={125} y={150} className="it-l3-m5" />
                <Home x={78} y={92} className="it-l3-m2" />
            </span>
            <Cursor className="it-l3-cur" />
        </>
    );
};

// Listvy 4: the photos swipe, the floor plan grows under the pointer, then the area's score and trend light up.
const ListvyCard = ({ sv }) => (
    <>
        <span className="it-panel it-l4-card">
            <span className="it-l4-photos">
                <span className="it-l4-strip">
                    <svg viewBox="0 0 136 164" aria-hidden="true">
                        <rect width="136" height="164" fill="#cfe3ef" /><rect y="118" width="136" height="46" fill="#9dbd8c" />
                        <circle cx="116" cy="98" r="18" fill="#6e9b5e" /><rect x="22" y="66" width="84" height="56" fill="#efe6d6" />
                        <path d="M14 70 64 32l50 38Z" fill="#a85a43" />
                        <rect x="32" y="78" width="14" height="14" fill="#6f8796" /><rect x="82" y="78" width="14" height="14" fill="#6f8796" />
                        <rect x="56" y="94" width="16" height="28" rx="7" fill="#7a5a43" />
                    </svg>
                    <svg viewBox="0 0 136 164" aria-hidden="true">
                        <rect width="136" height="164" fill="#efe4d3" />
                        <rect x="16" y="24" width="52" height="64" fill="#cfe3ef" stroke="#fff" strokeWidth="5" />
                        <rect y="124" width="136" height="40" fill="#c9a77f" /><rect x="56" y="98" width="70" height="30" rx="8" fill="#7a8f8a" />
                        <rect x="62" y="90" width="22" height="14" rx="5" fill="#a3b3ae" />
                        <path d="M108 0v34" stroke="#5f6c6a" strokeWidth="2" /><circle cx="108" cy="40" r="8" fill="#f3d58a" />
                    </svg>
                    <svg viewBox="0 0 136 164" aria-hidden="true">
                        <rect width="136" height="164" fill="#eef0ec" />
                        <rect x="0" y="34" width="136" height="34" fill="#fff" stroke="#dfe3df" />
                        <rect x="84" y="74" width="40" height="22" fill="#cfe3ef" stroke="#fff" strokeWidth="3" />
                        <rect x="0" y="98" width="136" height="8" fill="#8a7a66" /><rect x="0" y="106" width="136" height="58" fill="#f6f6f3" />
                        <path d="M34 106v58M68 106v58M102 106v58M34 34v34M68 34v34M102 34v34" stroke="#dfe3df" strokeWidth="1.5" />
                    </svg>
                </span>
                <span className="it-l4-dots"><i className="it-l4-d1" /><i className="it-l4-d2" /><i className="it-l4-d3" /></span>
                <span className="it-l4-plan">
                    <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                        <rect x="6" y="6" width="88" height="88" /><path d="M6 52h40M46 6v30M46 52v42M46 64h48M70 64v30" />
                        <path className="is-door" d="M46 36a16 16 0 0 1 16 16" />
                    </svg>
                    <span className="it-l4-plan-tag">{sv ? 'Planritning' : 'Floor plan'}</span>
                </span>
            </span>
            <b className="it-l4-address">Sveavägen 120</b>
            <span className="it-l4-place">Vasastan, Stockholm</span>
            <span className="it-l4-price">4 150 000 kr</span>
            <span className="it-l4-facts">{sv ? '3 rum · 75 m² · 4 280 kr/mån' : '3 rooms · 75 m² · 4 280 kr/mo'}</span>
            <span className="it-l4-rule" />
            <span className="it-l4-score it-l4-hs">
                <span className="it-badge is-small" style={scoreStyle(4.5)}>{decimal(4.5, sv)}</span>
                <span>{areaLabel(4.5, sv)}</span>
            </span>
            <span className="it-l4-trend it-l4-ht">↗ +18 % {sv ? 'i området' : 'in the area'}</span>
        </span>
        <Cursor className="it-l4-cur" />
    </>
);

// ---------- shared by both views ----------

// The welcome: a home lands, its area lights up, then the areas round it it's scored against; its score and word,
// then where that sits on the scale.
const WELCOME_AREAS = [
    { points: '0,0 120,0 95,80 0,90', score: 4, delay: 0 },
    { points: P2, score: 4.5, home: true },
    { points: P3, score: 3, delay: 0.18 },
    { points: '0,90 95,80 130,150 80,200 0,200', score: 3.5, delay: 0.72 },
    { points: P5, score: 5, delay: 0.36 },
    { points: '175,100 260,75 340,95 340,200 240,200 205,150', score: 2, delay: 0.54 },
    { points: '205,150 240,200 150,200', score: 5.5, delay: 0.9 },
];
const WELCOME_LINES = [[55, 40, 0], [295, 45, 0.18], [148, 140, 0.36], [270, 140, 0.54], [45, 150, 0.72]];

const Welcome = ({ sv }) => (
    <>
        <svg className="it-map" viewBox="0 0 340 200" aria-hidden="true">
            {WELCOME_AREAS.map(({ points, score, home, delay }) => (
                <polygon
                    key={points}
                    className={home ? 'it-g1-own' : 'it-g1-area'}
                    points={points}
                    fill={scoreToColor[score]}
                    stroke={scoreToColor[score]}
                    strokeWidth="1.4"
                    strokeLinejoin="round"
                    style={delay ? { animationDelay: `${delay}s` } : undefined}
                />
            ))}
            {WELCOME_LINES.map(([x, y, delay]) => (
                <path key={x} className="it-g1-line" d={`M150 56 L${x} ${y}`} style={delay ? { animationDelay: `${delay}s` } : undefined} />
            ))}
        </svg>
        <Ripple x={150} y={58} className="it-g1-rip" />
        <Home kind="villa" x={150} y={64} className="is-big it-g1-home" />
        <span className="it-g1-badge">
            <span className="it-badge is-small" style={scoreStyle(4.5)}>{decimal(4.5, sv)}</span>
            {areaLabel(4.5, sv)}
        </span>
        <span className="it-panel it-g1-card">
            <ScaleBar className="it-g1-scale" />
            <span className="it-mark it-g1-mk" />
            <span className="it-g1-end">1 · {scoreWord(1, sv)}</span>
            <span className="it-g1-end is-top">{scoreWord(6, sv)} · 6</span>
        </span>
    </>
);

// Restid: the trip draws itself from home to work and its time pops up; a limit greys out the homes too far away.
const TRIP = 'M56 146 C 96 146, 112 112, 150 106 S 226 70, 284 56';

const Commute = ({ sv }) => (
    <>
        <svg className="it-map" viewBox="0 0 340 200" aria-hidden="true">
            <path className="it-water" d="M-5 205 C 20 180, 60 186, 90 205 Z" />
            <g className="it-buildings">
                <rect x="20" y="22" width="16" height="11" rx="1" /><rect x="70" y="18" width="13" height="14" rx="1" />
                <rect x="150" y="30" width="18" height="12" rx="1" /><rect x="196" y="20" width="13" height="11" rx="1" />
                <rect x="230" y="90" width="20" height="13" rx="1" /><rect x="300" y="100" width="14" height="13" rx="1" />
                <rect x="30" y="100" width="15" height="12" rx="1" /><rect x="180" y="140" width="16" height="12" rx="1" />
                <rect x="270" y="170" width="18" height="11" rx="1" />
            </g>
            <g className="it-streets">
                <path className="is-casing" d="M-5 118 C 80 108, 200 126, 345 96" strokeWidth="9" />
                <path d="M-5 118 C 80 108, 200 126, 345 96" strokeWidth="6" />
                <path d="M120 -5 C 112 70, 140 140, 128 205" strokeWidth="4" />
                <path d="M250 -5 L262 205" strokeWidth="3.5" />
            </g>
            <path className="it-g2-route is-casing" d={TRIP} />
            <path className="it-g2-route" d={TRIP} />
        </svg>
        <Home x={118} y={46} className="it-g2-in" />
        <Home kind="villa" x={306} y={150} className="it-g2-in" />
        <Home x={34} y={76} className="it-g2-out" />
        <Home x={236} y={180} className="it-g2-out" />
        <Home kind="villa" filled x={56} y={150} className="is-big" />
        <span className="it-g2-work">
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="4" y="8" width="16" height="11" rx="2" />
                <path d="M9 8V6.5A1.5 1.5 0 0 1 10.5 5h3A1.5 1.5 0 0 1 15 6.5V8" />
            </svg>
        </span>
        <span className="it-g2-dot" />
        <span className="it-g2-chip">
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="6" y="3" width="12" height="14" rx="3" />
                <path d="M6 11h12M9 21l2-4M15 21l-2-4" />
            </svg>
            <b>24 min</b>
            <span>{sv ? 'Kollektivt' : 'Transit'}</span>
        </span>
        <span className="it-g2-bar">
            <span className="it-g2-v1">{sv ? 'Ingen gräns' : 'No limit'}</span>
            <b className="it-g2-v2">{sv ? 'Högst 30 min' : 'At most 30 min'}</b>
            <span className="it-g2-track" />
            <span className="it-g2-fill" />
            <span className="it-g2-knob" />
        </span>
        <Cursor className="it-g2-cur" />
    </>
);

// Lista eller karta: the cards shrink into their homes on the map while the search stays; and back.
const SwitchViews = ({ sv }) => {
    const rooms = (count) => (sv ? `${count} rum` : `${count} rooms`);
    return (
        <>
            <span className="it-g3-map"><AreaMap /></span>
            <Home x={90} y={100} className="it-g3-m" />
            <Home kind="villa" x={215} y={150} className="it-g3-m" delay=".1s" />
            <Home x={262} y={82} className="it-g3-m" delay=".2s" />
            <MiniCard className="it-g3-card it-g3-k1" style={{ top: 54 }} photo="flat" address="Råsundavägen 12" price={`3 450 000 kr · ${rooms(2)}`} score={4} sv={sv} />
            <MiniCard className="it-g3-card it-g3-k2" style={{ top: 102 }} photo="villa" address="Ritorpsvägen 10" price={`7 900 000 kr · ${rooms(5)}`} score={4} sv={sv} />
            <MiniCard className="it-g3-card it-g3-k3" style={{ top: 150 }} photo="flat" address="Huvudstagatan 4" price={`2 890 000 kr · ${rooms(2)}`} score={3} sv={sv} />
            <span className="it-g3-band" />
            <span className="it-chip it-g3-chip">Solna kommun<span aria-hidden="true">✕</span></span>
            <span className="it-g3-toggle">
                <span className="it-g3-slider" />
                <span className="it-g3-list">{sv ? 'Lista' : 'List'}</span>
                <span className="it-g3-karta">{sv ? 'Karta' : 'Map'}</span>
            </span>
            <Cursor className="it-g3-cur" />
        </>
    );
};

const copy = (sv, en) => ({ sv, en });

// The tips, in order. `target` finds the control a tip is about (its card sits beside it and a ring pulses round
// it; none: the middle of `area`), `placement` the sides to try, `phone: false` leaves it out on phones, `bg` the
// stage's ground (street | page; the map's by default), `reveal` scrolls the target into view first.
export const KARTVY_TIPS = [
    {
        id: 'pick',
        Scene: KartvyPick,
        still: 6.2,
        title: copy('Välj var du vill bo', 'Choose where to live'),
        text: copy('Sök eller klicka på ett område för att se bostäderna där.', 'Search, or click an area, to see the homes there.'),
        touchText: copy('Sök eller tryck på ett område för att se bostäderna där.', 'Search, or tap an area, to see the homes there.'),
        target: () => document.querySelector('.leaflet-geosearch-bar form') || document.querySelector('.leaflet-geosearch-bar'),
        placement: ['below'],
    },
    {
        id: 'score',
        Scene: KartvyScore,
        still: 3,
        title: copy('Färgen är betyget', 'The colour is the score'),
        text: copy('Peka på ett område för att se hur det står sig mot närområdet.', 'Point at an area to see how it compares with nearby areas.'),
        touchText: copy('Dubbeltryck på ett område för att se hur det står sig mot närområdet.', 'Double-tap an area to see how it compares with nearby areas.'),
        area: () => document.querySelector('.leaflet-container'),
    },
    {
        id: 'compare',
        Scene: KartvyCompare,
        still: 6,
        title: copy('Jämför områden', 'Compare areas'),
        text: copy('Dubbelklicka på områden för att ställa dem sida vid sida.', 'Double-click areas to put them side by side.'),
        touchText: copy('Dubbeltryck på ett område till för att ställa dem sida vid sida.', 'Double-tap another area to put them side by side.'),
        area: () => document.querySelector('.leaflet-container'),
    },
    {
        id: 'address',
        Scene: KartvyAddress,
        still: 6,
        bg: 'street',
        title: copy('Vad kostade huset?', 'What did it sell for?'),
        text: copy('Dra nålen till en adress för att se vad den sålts för.', 'Drag the pin to an address to see what it sold for.'),
        touchText: copy('Tryck på Adress och sedan på ett hus för att se vad det sålts för.', 'Tap Address, then a house, to see what it sold for.'),
        target: () => document.querySelector('.toolbar-place-btn'),
        placement: ['left', 'above'],
    },
];

// On screen: a size, and not hidden by `visibility` (listvy's filters wait hidden behind the landing).
const visible = (element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && element.checkVisibility?.({ visibilityProperty: true }) !== false;
};
const firstVisible = (selector) => [...document.querySelectorAll(selector)].find(visible) || null;

export const LISTVY_TIPS = [
    {
        id: 'places',
        Scene: ListvyPlaces,
        bg: 'page',
        still: 6,
        title: copy('Sök på flera ställen', 'Search several places'),
        text: copy('Lägg till orter, områden eller gator – de söks tillsammans.', 'Add towns, areas or streets – they are searched together.'),
        target: () => [...document.querySelectorAll('.list-view-search-form')]
            .find((form) => visible(form) && !form.closest('.list-view-landing')) || null,
        placement: ['below'],
        reveal: true,
    },
    {
        id: 'score-filter',
        Scene: ListvyScoreFilter,
        bg: 'page',
        still: 5.6,
        title: copy('Välj områden du gillar', 'Pick areas you like'),
        text: copy('Dra i färgskalan och tryck på Sök bostäder.', 'Drag the colour scale and press Search listings.'),
        target: () => firstVisible('.list-view-filter-column .drawer-section--area') || firstVisible('.list-view-mobile-chips .lv-chip'),
        placement: ['left', 'right', 'below'],
        reveal: true,
    },
    {
        id: 'map',
        Scene: ListvyMap,
        bg: 'page',
        still: 6.8,
        phone: false,
        title: copy('Var ligger den?', 'Where is it?'),
        text: copy('Peka på en bostad för att se den på kartan.', 'Point at a home to see it on the map.'),
        touchText: copy('Tryck på Visa på kartan för att se bostaden på kartan.', 'Tap Show on map to see the home on the map.'),
        target: () => firstVisible('.list-view-map-column:not(.is-collapsed) .list-view-map-shell')
            || firstVisible('.list-view-map-column:not(.is-collapsed)')
            || firstVisible('.list-view-map-column-tab'),
        placement: ['right', 'left', 'below', 'above'],
    },
    {
        id: 'card',
        Scene: ListvyCard,
        bg: 'page',
        still: 6.6,
        title: copy('Allt på kortet', 'It’s all on the card'),
        text: copy('Bläddra bland bilderna, peka på planritningen och se områdets betyg.', 'Browse the photos, point at the floor plan and see the area’s score.'),
        touchText: copy('Svep bland bilderna, håll på planritningen och se områdets betyg.', 'Swipe the photos, hold the floor plan and see the area’s score.'),
        target: () => firstVisible('.list-view-card'),
        placement: ['right', 'left', 'below', 'above'],
        reveal: true,
    },
];

// The welcome, before a view's series the first time on the site, whichever view that is (`key`: seen once for
// both; `solo`: not one of the series' dots). Visa mig goes on to the view's tips, Hoppa över ends them all.
export const WELCOME_TIP = {
    id: 'welcome',
    key: 'welcome',
    solo: true,
    Scene: Welcome,
    still: 6,
    title: copy('Rätt bostad i rätt område', 'The right home in the right area'),
    text: copy('Varje område får ett betyg från 1 till 6 jämfört med områdena runt omkring.', 'Every area gets a score from 1 to 6, compared with the areas around it.'),
    skip: copy('Hoppa över', 'Skip'),
    next: copy('Visa mig', 'Show me'),
    // Over the middle of kartvy's map; in listvy the middle of the window.
    area: () => (document.querySelector('.list-view-page') ? null : document.querySelector('.leaflet-container')),
};

// The shared tips, each shown once when its moment comes (requestIntroTip; SharedIntroTips in IntroTips.js):
// `requireTarget` drops one whose control isn't there (and closes it if that goes), `afterSeries` waits for the
// view's own series (the first search starts listvy's), `ready` asks anything else,
// `raised` puts it over the views' filter sheets, `onNext` is its main button's action, `doneOnFocus` ends it once
// the focus is in its control.
export const SHARED_TIPS = {
    // The first time the filters open (a drawer, sheet or panel; listvy's always-open column at the first touch,
    // CommuteFilter.js) with no workplace set.
    commute: {
        id: 'commute',
        Scene: Commute,
        bg: 'street',
        still: 6,
        title: copy('Restid till jobbet', 'Travel time to work'),
        text: copy('Lägg in var du jobbar för att se restiden från varje bostad.', 'Add where you work to see the travel time from every home.'),
        skip: copy('Inte nu', 'Not now'),
        next: copy('Lägg in arbetsplats', 'Add workplace'),
        target: () => firstVisible('.commute-filter'),
        ready: () => Boolean(firstVisible('.commute-filter .commute-input')),
        requireTarget: true,
        raised: true,
        doneOnFocus: true,
        reveal: true,
        placement: ['left', 'right', 'below', 'above'],
        onNext: () => firstVisible('.commute-filter .commute-input')?.focus(),
    },
    // After a search (both views), pointing at the way to the other view; switching views counts as seen (App.js).
    switch: {
        id: 'switch',
        Scene: SwitchViews,
        bg: 'page',
        still: 5.4,
        title: copy('Lista eller karta', 'List or map'),
        text: copy('Byt vy när du vill – sökningen följer med.', 'Switch views any time – your search comes along.'),
        next: copy('Okej', 'OK'),
        target: () => firstVisible('.list-view-kartvy-btn')
            || firstVisible('.lv-chip--map')
            || document.querySelector('.map-topright-btn.listViewButtonContainer')
            || null,
        requireTarget: true,
        afterSeries: true,
        reveal: true,
        placement: ['below', 'left'],
    },
};
